/**
 * Numbers as the product shows them.
 *
 * One formatter for every count, axis tick and tile, because each screen used to carry its own:
 * a `k`-only one turned 13.3 billion into "13266076.5k", and a `Math.round` one turned 999,999
 * into "1000K". `Intl` already knows thousands, millions and billions, and rounds across them.
 */

const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const FULL = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/** `-` for "not known", never a zero: a zero is a claim. */
export const NO_VALUE = '-';

const known = (value: number | null | undefined): value is number =>
	typeof value === 'number' && Number.isFinite(value);

/** 950 → "950", 1,250 → "1.3K", 13,266,076,500 → "13.3B". */
export function formatCompact(value: number | null | undefined, fallback = NO_VALUE): string {
	return known(value) ? COMPACT.format(value) : fallback;
}

/** Every digit, grouped: 13266076.5 → "13,266,076.5". For a tooltip or a detail row. */
export function formatFull(value: number | null | undefined, fallback = NO_VALUE): string {
	return known(value) ? FULL.format(value) : fallback;
}

/**
 * `part` as a share of `total`, e.g. "53.4%". An empty total is "0%", not a division by zero.
 * `digits` is how many decimals to keep; trailing zeros are dropped, so 53.0 reads "53%".
 *
 * A share that is not zero never rounds to "0%" and one that is not whole never rounds to
 * "100%": 444.5K of 13.3B tokens read "0.0%" used, which says nothing was used. They read
 * "<1%" and ">99%" instead (with `digits` 1, "<0.1%" and ">99.9%").
 */
export function formatPercent(part: number | null | undefined, total = 100, digits = 0, fallback = NO_VALUE): string {
	if (!known(part)) return fallback;
	const share = total > 0 ? (part / total) * 100 : 0;
	const rounded = parseFloat(share.toFixed(digits));
	const step = 1 / 10 ** digits;
	if (share > 0 && rounded === 0) return `<${step}%`;
	if (share < 100 && rounded === 100) return `>${parseFloat((100 - step).toFixed(digits))}%`;
	return `${rounded}%`;
}

export type ScoreTone = 'success' | 'warning' | 'danger';

/**
 * A 0-100 score as a tone: good from `good` up, fair from `fair` up, poor below.
 *
 * One rule instead of a dozen inline ones that mixed `>` and `>=` and hard-coded 70 in one
 * place and 75 in another, so the same score read green on one screen and amber on the next.
 */
export function scoreTone(score: number, { good = 70, fair = 40 }: { good?: number; fair?: number } = {}): ScoreTone {
	if (score >= good) return 'success';
	if (score >= fair) return 'warning';
	return 'danger';
}
